import React, { useEffect, useMemo, useState } from 'react';
import {
  Background,
  Controls,
  MiniMap,
  ReactFlow,
  ReactFlowProvider,
  type Edge,
  type Node,
  type NodeMouseHandler,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { parseTaskflowXml, type TaskNodeData } from './TaskflowParser';
import { layoutGraph } from './elkLayout';
import { TaskNode } from './nodes/TaskNode';
import { ConditionNode } from './nodes/ConditionNode';
import { TaskDetailDialog } from './TaskDetailDialog';

const nodeTypes = {
  taskNode: TaskNode,
  conditionNode: ConditionNode,
};

interface Props {
  xml: string;
}

export function TaskflowGraph({ xml }: Props): React.JSX.Element {
  const parsed = useMemo(() => parseTaskflowXml(xml), [xml]);
  const [nodes, setNodes] = useState<Node[]>([]);
  const [edges, setEdges] = useState<Edge[]>([]);
  const [layouting, setLayouting] = useState(true);
  const [selected, setSelected] = useState<TaskNodeData | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLayouting(true);
    layoutGraph(parsed.nodes, parsed.edges).then(result => {
      if (cancelled) return;
      setNodes(result.nodes);
      setEdges(result.edges);
      setLayouting(false);
    });
    return () => {
      cancelled = true;
    };
  }, [parsed]);

  const onNodeDoubleClick: NodeMouseHandler = (_event, node) => {
    if (node.type === 'taskNode') {
      setSelected(node.data as TaskNodeData);
    }
  };

  if (parsed.nodes.length === 0) {
    return (
      <div className="taskflow-graph-empty">
        <p>No taskflow to visualize.</p>
        {parsed.diagnostics.length > 0 && (
          <ul>
            {parsed.diagnostics.map((d, i) => (
              <li key={i}>{d}</li>
            ))}
          </ul>
        )}
      </div>
    );
  }

  return (
    <div className="taskflow-graph">
      {parsed.taskflowName && (
        <div className="taskflow-graph__title">
          <strong>{parsed.taskflowName}</strong>
          {parsed.diagnostics.length > 0 && (
            <span className="taskflow-graph__warnings" title={parsed.diagnostics.join('\n')}>
              <i className="fa-solid fa-triangle-exclamation" /> {parsed.diagnostics.length} warning(s)
            </span>
          )}
        </div>
      )}
      {layouting ? (
        <div className="taskflow-graph__loading">Laying out graph…</div>
      ) : (
        <ReactFlowProvider>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            nodeTypes={nodeTypes}
            fitView
            nodesDraggable={false}
            nodesConnectable={false}
            elementsSelectable
            onNodeDoubleClick={onNodeDoubleClick}
            proOptions={{ hideAttribution: true }}
          >
            <Background />
            <Controls showInteractive={false} />
            <MiniMap pannable zoomable />
          </ReactFlow>
        </ReactFlowProvider>
      )}
      <TaskDetailDialog data={selected} onClose={() => setSelected(null)} />
    </div>
  );
}
